<template>
  <div class="histogram">
    <div
      v-if="showLegend || showNavigation"
      class="header"
    >
      <histogram-legend v-if="showLegend" />
      <div
        v-if="showNavigation"
        class="buttons"
      >
        <div
          tabindex="0"
          class="btn left"
          :class="{ disabled: leftDisabled }"
          @click="scrollLeft"
          @keyup.enter="scrollLeft"
        />
        <div
          tabindex="0"
          class="btn right"
          :class="{ disabled: rightDisabled }"
          @click="scrollRight"
          @keyup.enter="scrollRight"
        />
      </div>
    </div>

    <h3
      v-if="histogramTitle"
      class="histogram-title"
    >
      {{ histogramTitle }}
    </h3>

    <div
      ref="histogram-content"
      v-dragscroll="isMobile"
      class="histogram-content"
    >
      <div
        v-for="(month, index) in priceList"
        :key="index"
        class="histogram-wrap"
      >
        <template v-if="hasPrice(month.price)">
          <div
            class="histogram-item"
            :class="{ 'has-direct': month.hasDirect, 'wo-tooltip': disableTooltip }"
            @click="openFSF(month)"
          >
            <div
              v-dompurify-html="formatPrice(month.price)"
              class="column"
              :style="{ height: getHeight(month.price) }"
            />
          </div>
        </template>
        <template v-else>
          <div class="column no-flights" />
        </template>

        <span v-dompurify-html="getMonthName(month.id)" />
        <div
          class="balloon"
          :class="deviceType"
        >
          {{ $t('destinationsSilo.oneWayPrice') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment'
  import max from 'lodash-es/max'
  import { mapState } from 'vuex'
  import { dragscroll } from 'vue-dragscroll'
  import HistogramService from 'services/AirBaltic/Destinations/Histogram'
  import FsfMixin from 'components/FlightSearchForm/FsfMixin'
  import OrigDestUtils from 'store/utils/eHorizonOrigDestUtils'

  export default {
    components: {
      'histogram-legend': () =>
        import(
          /* webpackChunkName: "histogram-legend" */ 'components/Histogram/HistogramLegend'
        )
    },
    directives: {
      dragscroll
    },
    mixins: [FsfMixin],
    props: {
      pricesFromParent: {
        type: Array,
        default: () => []
      },
      destinationCode: {
        type: String,
        required: true
      },
      originCode: {
        type: String,
        required: true
      },
      selectedLabel: {
        type: String,
        default: '',
        required: false
      },
      tripType: {
        type: String,
        default: 'oneway'
      },
      numAdt: {
        type: Number,
        default: 1
      },
      numYth: {
        type: Number,
        default: 0
      },
      numChd: {
        type: Number,
        default: 0
      },
      numInf: {
        type: Number,
        default: 0
      },
      promo: {
        type: Object,
        default: () => ({})
      },
      sref: {
        type: String,
        default: 'BXD'
      },
      isGroupBooking: {
        type: Boolean,
        default: false
      },
      groupBookingUrl: {
        type: String,
        default: ''
      },
      columnMaxHeightInPixels: {
        type: Number,
        default: 56,
        required: false
      },
      showNavigation: {
        type: Boolean,
        default: false,
        required: false
      },
      disableTooltip: {
        type: Boolean,
        default: false,
        required: false
      },
      showLegend: {
        type: Boolean,
        default: false,
        required: false
      },
      histogramTitle: {
        type: String,
        default: ''
      },
      fullScroll: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        leftDisabled: true,
        rightDisabled: false,
        prices: []
      }
    },
    computed: {
      ...mapState('device', ['deviceType']),
      isMobile() {
        return this.$store.getters['device/isMobile']
      },
      priceList() {
        return this.pricesFromParent && this.pricesFromParent.length
          ? this.pricesFromParent
          : this.prices
      },
      columnWidth() {
        const months =
          this.$refs['histogram-content'].getElementsByClassName('histogram-wrap')
        if (months.length) {
          return months[0].clientWidth
        }

        return 0
      },
      scrollDistance() {
        const container = this.$refs['histogram-content']
        const fullColumnsInSight = Math.floor(container.clientWidth / this.columnWidth)
        return fullColumnsInSight * this.columnWidth
      },
      adultCount() {
        return this.numAdt + this.numYth
      }
    },
    methods: {
      hasPrice(price) {
        return parseInt(price) >= 0
      },
      getHeight(price) {
        const priceInt = parseInt(price)

        if (
          priceInt === null ||
          priceInt === 0 ||
          isNaN(priceInt) ||
          typeof priceInt === 'undefined'
        ) {
          return '0px'
        }

        const divider = max(this.priceList.map((a) => a.price)) ?? 12

        return Math.trunc((priceInt * this.columnMaxHeightInPixels) / divider) + 'px'
      },
      formatPrice(price) {
        if (price === null || price === 0) {
          return ''
        }

        return Math.trunc(price) + '<sup>99</sup>€'
      },
      getMonthName(monthId) {
        const dateInst = new moment().add(monthId - 2, 'months').date(1)

        return moment.monthsShort(parseInt(dateInst.format('M')))
      },

      openFSF(month) {
        if (this.promo && this.promo.invalid) {
          this.$emit('promoError')
          return
        } else {
          const newDate = new moment().add(month.id - 1, 'M')
          this.$emit('histogramSearch', newDate.format('M-YYYY'))
        }

        this.$_fsfMixin_initFsfModal()
        this.setFSFData(month.id)
        this.$_fsfMixin_openFsfModal(false, true, true)
      },

      setFSFData(month) {
        let tripType = 'oneway'
        let voucher = this.promo?.promoCodes ? this.promo.promoCodes : []

        const origin = this.origDestData[this.originCode + 'A']
        const destinations = OrigDestUtils.extractDestinations(origin, this.origDestData)
        const destin = destinations.find((dest) => dest.code === this.destinationCode)

        if (!isNaN(month)) {
          this.$store.commit('eHorizonSearchForm/setSelectedMonth', Number(month - 1))
        }
        this.$_fsfMixin_setVouchers(voucher)
        this.$_fsfMixin_selectOrigin(origin, false)
        this.$_fsfMixin_selectDestin(destin, false)
        this.$store.commit('eHorizonSearchForm/setTripType', tripType)
        this.$store.commit('eHorizonSearchForm/setSref', this.sref)

        let postObj = {
          origin: this.originCode,
          destination: this.destinationCode
        }

        this.$_fsfMixin_getOnewayYearData(postObj)
      },

      getHistogramPrices() {
        if (this.originCode && this.destinationCode) {
          return new Promise((resolve, reject) => {
            new HistogramService()
              .getHistogramData(this.originCode, this.destinationCode)
              .then((prices) => {
                this.prices = prices
                resolve(prices)
              })
              .catch((e) => {
                this.prices = []
                reject(e)
              })
          })
        } else {
          this.prices = []
        }
      },

      resetPrices() {
        this.prices = []
      },

      scrollLeft() {
        if (this.leftDisabled) {
          return
        }
        const container = this.$refs['histogram-content']
        this.rightDisabled = false
        if (this.fullScroll) {
          this.scrollFullLeft()
        } else {
          const diff = container.scrollLeft - this.scrollDistance
          container.scrollLeft = diff
          if (diff <= 0) {
            this.scrollFullLeft()
          }
        }
      },

      scrollRight() {
        if (this.rightDisabled) {
          return
        }
        const container = this.$refs['histogram-content']
        this.leftDisabled = false
        if (this.fullScroll) {
          this.scrollFullRight()
        } else {
          let scrollLeft = container.scrollLeft
          let left = scrollLeft + this.scrollDistance
          container.scrollLeft = left
          if (left + this.scrollDistance >= container.scrollWidth - this.columnWidth) {
            this.scrollFullRight()
          }
        }
      },

      scrollFullLeft() {
        const container = this.$refs['histogram-content']
        this.leftDisabled = true
        container.scrollLeft = 0
      },

      scrollFullRight() {
        const container = this.$refs['histogram-content']
        this.rightDisabled = true
        container.scrollLeft = container.scrollWidth
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  .histogram {
    display: flex;
    justify-content: space-between;
    flex-direction: column;

    .histogram-content {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      flex-wrap: nowrap;
      overflow: auto;

      &::-webkit-scrollbar {
        display: none;
      }

      // FF specific to hide scrollbar
      scrollbar-width: none;

      @include bp-mf-desktop {
        overflow: visible;
      }
    }

    .histogram-wrap {
      position: relative;
      padding-right: 8px;

      @include bp-mf-tablet {
        padding-right: 12px;
      }

      &:last-of-type {
        padding-right: 17px;

        @include bp-mf-tablet {
          padding-right: 20px;
        }
      }
    }

    .no-flights {
      text-align: center;
      width: 52px;

      &:after {
        color: #cccccc;
        font-family: $icon-font-family;
        content: $icon-no-flights;
        font-size: rem(20, 16);
      }
    }

    .histogram-item {
      border-radius: 3px;
      background-color: #dce8f5;
      display: block;
      color: $blue-close;
      font-size: rem(12, 16);
      text-decoration: none;
      transition: $linearTransition03;
      cursor: pointer;

      &.has-direct {
        background-color: #f4f7d0;
      }

      .column {
        padding-bottom: 9px;
        line-height: rem(12, 16);
        display: flex;
        align-items: flex-end;
        width: 44px;
        flex-grow: 1;
        justify-content: center;
      }

      ::v-deep sup {
        font-size: rem(6, 16);
        position: relative;
        top: -8px;
      }

      &:hover {
        background-color: #afbfd0;

        &.has-direct {
          background-color: #dfe876;
        }

        ~ .balloon:not(.mobile) {
          @include bp-mf-desktop {
            display: block;
          }
        }
      }

      &.wo-tooltip {
        &:hover {
          ~ .balloon {
            display: none !important;
          }
        }
      }
    }

    .balloon {
      padding: 5px;
      border-radius: 3px;
      background-color: #3a3f82;
      box-shadow: 0 2px 6px 0 rgba(173, 173, 173, 0.5);
      color: white;
      font-size: rem(11, 16);
      line-height: rem(13, 16);
      text-align: center;
      position: absolute;
      display: none;
      bottom: -30px;
      transform: translateX(-50%);
      left: 50%;
      white-space: nowrap;
      z-index: 1;

      &:after {
        position: absolute;
        content: '';
        top: -10px;
        @include triangle(5px, $blue-medium, 'up');
        left: 50%;
        transform: translate(-50%, 0%);
      }

      &:hover {
        display: block;
      }
    }

    span {
      margin: 7px 0 0;
      display: flex;
      color: #aeb4ba;
      font-size: rem(11, 16);
      font-weight: 500;
      line-height: rem(13, 16);
      text-align: center;
      justify-content: center;
    }

    .buttons {
      display: flex;
      width: 70px;
      flex-direction: row;
      justify-content: space-between;
      position: relative;
      padding-top: 10px;

      @include bp-mf-desktop {
        padding-top: 0;
      }

      .btn {
        display: flex;
        width: 30px;
        height: 30px;
        border: 1px solid $blue-medium;
        min-width: 30px;
        border-radius: 50%;
        align-items: center;
        justify-content: center;
        cursor: pointer;

        &.disabled {
          border: 1px solid rgba($blue-medium, 0.2);
          cursor: default;

          &::before {
            color: rgba($blue-medium, 0.2);
          }
        }

        &::before {
          content: $icon-arrow-right-thin-medium;
          @include icon-font-thin;
          color: $blue-medium;
          font-size: rem(14, 16);
        }

        &.left {
          &::before {
            transform: rotate(180deg);
          }
        }
      }
    }
  }

  .header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    order: 1;

    .histogram-legend {
      width: 85%;

      @include bp-mf-tablet {
        width: 50%;
      }
    }

    @include bp-mf-desktop {
      order: 0;
      margin-right: 16px;
      margin-bottom: 23px;
    }
  }

  .histogram-title {
    font-size: rem(16, 16);
    line-height: rem(19);
    color: $blue-dark;
    font-weight: 500;

    @include bp-mf-tablet {
      margin-bottom: 24px;
      font-size: rem(20, 16);
    }
  }
</style>
